import { useAutoAnimate } from "@formkit/auto-animate/react";
import React from "react";
import { useMutation } from "react-query";
import { Report } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useSearch } from "../../../hooks/useSearch";
import { NoResults } from "../../Common/NoResults";
import { ContentLayout } from "../../Layout/ContentLayout";
import { ReportTile } from "./ReportTile";
import { SuggestedReport, useSuggestedReports } from "./SuggestedReports";

export const CustomReportList = ({ reports, search }: { reports: Report[]; search: string }) => {
    const suggestedReports = useSuggestedReports();

    const filteredSuggestedReports = useSearch(
        suggestedReports,
        (report) => [report.report.title, report.description],
        search,
    );

    return (
        <ContentLayout isWide className="space-y-20">
            <ReportList reports={reports} title="Saved Reports" suggestedReports={suggestedReports} />
            {reports.length > 0 && <hr className="text-gray-light" />}
            <ReportList
                reports={filteredSuggestedReports.map(({ report }) => report)}
                title="Suggested Reports"
                suggestedReports={suggestedReports}
            />
        </ContentLayout>
    );
};

interface ReportListProps {
    title: string;
    reports: Report[];
    suggestedReports: SuggestedReport[];
}

const ReportList = ({ title, reports, suggestedReports }: ReportListProps) => {
    const [animationParent] = useAutoAnimate<HTMLDivElement>();

    const seller = useSeller();
    const invalidate = useInvalidate();
    const { mutateAsync: deleteReport, isLoading: isDeleting } = useMutation(async (id: ID) => {
        return api.delete(`/sellers/${seller.id}/reports/${id}`);
    });

    const handleReportDelete = async (reportId?: ID) => {
        if (reportId) {
            await deleteReport(reportId);
            invalidate("reports");
        }
    };

    if (reports.length === 0) {
        return (
            <NoResults>
                You do not have any saved reports yet. To save a new report, select a Suggested Report, make a
                modification, then click &quot;Save Report&quot;
            </NoResults>
        );
    }

    if (reports.length > 0) {
        return (
            <div>
                <h2 className="mb-6">{title}</h2>

                <div ref={animationParent} className="grid grid-cols-2 gap-x-4 gap-y-6 xl:grid-cols-3 2xl:grid-cols-4">
                    {reports.map((report) => (
                        <ReportTile
                            key={report.id ?? report.object}
                            report={report}
                            isDeleting={isDeleting}
                            suggestedReports={suggestedReports}
                            onDelete={handleReportDelete}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return null;
};
